import React, { useState, useRef, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import '../../../styles/resume-editor.css';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import { showToast } from '../../../utils/toastManager';

interface SkillsSectionProps {
  initialSkills?: string[];
  initialCategories?: string[];
  initialCategorySkills?: Record<string, string[]>;
  onSkillsChange?: (skills: string[]) => void;
  onCategoriesChange?: (categories: string[]) => void;
  onCategorySkillsChange?: (categorySkills: Record<string, string[]>) => void;
  cvUid?: string;
  skillGroupsData?: Array<{ uid: string; skill_group_name: string; index: number; skills: string[] }>;
  skillsStructureUid?: string | null;
  onSkillGroupCreated?: () => void;
}

export const SkillsSection: React.FC<SkillsSectionProps> = ({
  initialSkills = [],
  initialCategories = [],
  initialCategorySkills = {},
  onSkillsChange,
  onCategoriesChange,
  onCategorySkillsChange,
  cvUid,
  skillGroupsData = [],
  skillsStructureUid,
  onSkillGroupCreated,
}) => {
  const [skills, setSkills] = useState<string[]>(initialSkills);
  const [skillSearch, setSkillSearch] = useState('');
  const [skillError, setSkillError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [categoryMessage, setCategoryMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [categories, setCategories] = useState<string[]>(initialCategories);
  const [categorySkills, setCategorySkills] = useState<Record<string, string[]>>(initialCategorySkills);
  const [openCategoryMenu, setOpenCategoryMenu] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const categoryMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [draggedSkill, setDraggedSkill] = useState<{ skill: string; source: string | null } | null>(null);
  const [draggedSkillIndex, setDraggedSkillIndex] = useState<number | null>(null);
  const [dragOverSkillIndex, setDragOverSkillIndex] = useState<number | null>(null);
  const [draggedCategorySkill, setDraggedCategorySkill] = useState<{ category: string; index: number } | null>(null);
  const [dragOverCategorySkill, setDragOverCategorySkill] = useState<{ category: string; index: number } | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);

  const isDraggingRef = useRef<boolean>(false);

  useEffect(() => {
    onSkillsChange?.(skills);
  }, [skills, onSkillsChange]);

  useEffect(() => {
    onCategoriesChange?.(categories);
  }, [categories, onCategoriesChange]);

  useEffect(() => {
    onCategorySkillsChange?.(categorySkills);
  }, [categorySkills, onCategorySkillsChange]);

  useEffect(() => {
    setCategories(initialCategories);
    setCategorySkills(initialCategorySkills);
    setSkills(initialSkills);
  }, [initialCategories, initialCategorySkills, initialSkills]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openCategoryMenu) {
        const menuElement = categoryMenuRefs.current[openCategoryMenu];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenCategoryMenu(null);
        }
      }
    };

    if (openCategoryMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openCategoryMenu]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [toast]);

  useEffect(() => {
    const handleGlobalDragStart = () => {
      isDraggingRef.current = true;
    };

    const handleGlobalDragEnd = () => {
      isDraggingRef.current = false;
    };

    const handleGlobalDragOver = (e: DragEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'move';
        }
      }
    };

    document.addEventListener('dragstart', handleGlobalDragStart);
    document.addEventListener('dragend', handleGlobalDragEnd);
    document.addEventListener('dragover', handleGlobalDragOver);

    return () => {
      document.removeEventListener('dragstart', handleGlobalDragStart);
      document.removeEventListener('dragend', handleGlobalDragEnd);
      document.removeEventListener('dragover', handleGlobalDragOver);
    };
  }, []);

  const clearDragState = () => {
    setDraggedSkill(null);
    setDraggedSkillIndex(null);
    setDragOverSkillIndex(null);
    setDraggedCategorySkill(null);
    setDragOverCategorySkill(null);
    setDragOverCategory(null);
  };

  const updateCategorySkills = async (categoryName: string, newSkills: string[]) => {
    if (!cvUid) {
      throw new Error('cvUid is missing');
    }
    
    const skillGroup = skillGroupsData.find(
      (group) => group.skill_group_name === categoryName
    );
    
    if (!skillGroup || !skillGroup.uid) {
      throw new Error(`Skill group not found for category: ${categoryName}`);
    }

    try {
      const response = await api.put(API_ENDPOINTS.cvs.updateSkills(cvUid), {
        uid: skillGroup.uid,
        skills: newSkills,
      });
      return response;
    } catch (err: any) {
      setToast({ 
        message: err?.response?.data?.detail || 'Failed to update skills', 
        type: 'error' 
      });
      throw err;
    }
  };

  const handleAddSkill = async () => {
    const trimmedValue = skillSearch.trim();
    if (trimmedValue) {
      const defaultCategory = skillGroupsData.find(
        (group) => group.index === 0
      );
      
      if (!defaultCategory || !defaultCategory.skill_group_name || !defaultCategory.uid) {
        setToast({ message: 'Default category not found', type: 'error' });
        setSkillError('Default category not found');
        return;
      }

      const skillExists = Object.values(categorySkills).some(
        (categorySkillList) => categorySkillList.includes(trimmedValue)
      );
      
      if (skillExists) {
        setToast({ message: 'This skill already exists', type: 'error' });
        setSkillError('This skill already exists');
        return;
      }

      const updatedSkills = [
        ...(categorySkills[defaultCategory.skill_group_name] || []),
        trimmedValue
      ];
      
      setCategorySkills(prev => ({
        ...prev,
        [defaultCategory.skill_group_name]: updatedSkills
      }));
      setSkillSearch('');
      setSkillError(null);
      setToast(null);

      if (cvUid) {
        try {
          await api.put(API_ENDPOINTS.cvs.updateSkills(cvUid), {
            uid: defaultCategory.uid,
            skills: updatedSkills,
          });
        } catch (err: any) {
          setToast({ 
            message: err?.response?.data?.detail || 'Failed to update skills', 
            type: 'error' 
          });
          setCategorySkills(prev => ({
            ...prev,
            [defaultCategory.skill_group_name]: (prev[defaultCategory.skill_group_name] || []).filter(s => s !== trimmedValue)
          }));
        }
      }
    }
  };

  const handleAddCategory = async () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) return;
    
    if (categories.includes(trimmedName)) {
      setCategoryMessage({ text: 'A category with this name already exists', type: 'error' });
      setCategoryError('A category with this name already exists');
      return;
    }

    if (!cvUid) {
      setCategoryMessage({ text: 'Unable to create category. Please refresh the page and try again.', type: 'error' });
      setCategoryError('Unable to create category');
      return;
    }

    if (!skillsStructureUid) {
      setCategoryMessage({ text: 'Unable to create category. Please refresh the page and try again.', type: 'error' });
      setCategoryError('Unable to create category');
      return;
    }

    const maxIndex = skillGroupsData.length > 0 
      ? Math.max(...skillGroupsData.map(g => g.index))
      : -1;
    const newIndex = maxIndex + 1;

    try {
      await api.post(API_ENDPOINTS.cvs.createSkillsGroup(cvUid), {
        skill_group_name: trimmedName,
        index: newIndex,
        structure_uid: skillsStructureUid,
      });

      setCategories([...categories, trimmedName]);
      setCategorySkills(prev => ({ ...prev, [trimmedName]: [] }));
      setNewCategoryName('');
      setIsAddingCategory(false);
      setCategoryError(null);
      setCategoryMessage({ text: `Category "${trimmedName}" created successfully!`, type: 'success' });
      
      setTimeout(() => {
        setCategoryMessage(null);
      }, 3000);
      
      onSkillGroupCreated?.();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail;
      setCategoryMessage({ 
        text: errorMessage && typeof errorMessage === 'string' && errorMessage.length < 100
          ? errorMessage
          : 'Couldn\'t create category. Please try again.',
        type: 'error'
      });
      setCategoryError('Couldn\'t create category');
    }
  };

  const handleRenameCategory = async (oldName: string, newName: string) => {
    if (!newName || newName === oldName || categories.includes(newName)) {
      setEditingCategory(null);
      setEditingCategoryName('');
      return;
    }

    if (!cvUid) {
      setToast({ message: 'CV UID is required', type: 'error' });
      setEditingCategory(null);
      setEditingCategoryName('');
      return;
    }

    const skillGroup = skillGroupsData.find(
      (group) => group.skill_group_name === oldName
    );

    if (!skillGroup || !skillGroup.uid) {
      setToast({ message: 'Skill group not found', type: 'error' });
      setEditingCategory(null);
      setEditingCategoryName('');
      return;
    }

    const newCategories = categories.map(c => c === oldName ? newName : c);
    setCategories(newCategories);
    if (categorySkills[oldName]) {
      setCategorySkills(prev => {
        const newSkills = { ...prev };
        newSkills[newName] = prev[oldName];
        delete newSkills[oldName];
        return newSkills;
      });
    }

    setEditingCategory(null);
    setEditingCategoryName('');

    try {
      await api.put(API_ENDPOINTS.cvs.updateSkillGroup(cvUid), {
        uid: skillGroup.uid,
        type: 'update',
        skill_group_name: newName,
      });
      onSkillGroupCreated?.();
    } catch (err: any) {
      setToast({ 
        message: err?.response?.data?.detail || 'Failed to rename skill group', 
        type: 'error' 
      });
      setCategories(prev => prev.map(c => c === newName ? oldName : c));
      if (categorySkills[newName]) {
        setCategorySkills(prev => {
          const newSkills = { ...prev };
          newSkills[oldName] = prev[newName];
          delete newSkills[newName];
          return newSkills;
        });
      }
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    const isDefaultCategory = skillGroupsData.some(
      (group) => group.skill_group_name === categoryName && group.index === 0
    );
    
    if (isDefaultCategory) {
      return;
    }

    if (!cvUid) {
      setToast({ message: 'CV UID is required', type: 'error' });
      return;
    }

    const skillGroup = skillGroupsData.find(
      (group) => group.skill_group_name === categoryName
    );

    if (!skillGroup || !skillGroup.uid) {
      setToast({ message: 'Skill group not found', type: 'error' });
      return;
    }

    const originalCategories = [...categories];
    const originalCategorySkills = { ...categorySkills };

    const defaultCategory = skillGroupsData.find(
      (group) => group.index === 0
    );
    
    const skillsToMove = categorySkills[categoryName] || [];
    if (defaultCategory && defaultCategory.skill_group_name) {
      setCategorySkills(prev => ({
        ...prev,
        [defaultCategory.skill_group_name]: [
          ...(prev[defaultCategory.skill_group_name] || []),
          ...skillsToMove
        ]
      }));

      try {
        const updatedDefaultSkills = [
          ...(categorySkills[defaultCategory.skill_group_name] || []),
          ...skillsToMove
        ];
        await updateCategorySkills(defaultCategory.skill_group_name, updatedDefaultSkills);
      } catch (err) {
        setCategorySkills(prev => ({
          ...prev,
          [defaultCategory.skill_group_name]: (prev[defaultCategory.skill_group_name] || []).slice(0, -skillsToMove.length)
        }));
        return;
      }
    }

    setCategories(prev => prev.filter(c => c !== categoryName));
    setCategorySkills(prev => {
      const newSkills = { ...prev };
      delete newSkills[categoryName];
      return newSkills;
    });
    setOpenCategoryMenu(null);

    try {
      await api.put(API_ENDPOINTS.cvs.updateSkillGroup(cvUid), {
        uid: skillGroup.uid,
        type: 'delete',
      });
      onSkillGroupCreated?.();
    } catch (err: any) {
      setToast({ 
        message: err?.response?.data?.detail || 'Failed to delete skill group', 
        type: 'error' 
      });
      setCategories(originalCategories);
      setCategorySkills(originalCategorySkills);
    }
  };

  const renderSkillChip = (
    skill: string,
    index: number,
    source: string | null,
    isDragging: boolean,
    isDragOver: boolean
  ) => {
    const isFromCategory = source !== null;
    const category = source;

    return (
      <div
        key={`${source || 'other'}-${index}`}
        className={`resume-editor__other-skill-tag ${isDragging ? 'resume-editor__other-skill-tag--dragging' : ''} ${isDragOver ? 'resume-editor__other-skill-tag--drag-over' : ''}`}
        draggable={true}
        onDragStart={(e) => {
          isDraggingRef.current = true;
          setDraggedSkill({ skill, source });
          if (isFromCategory && category) {
            setDraggedCategorySkill({ category, index });
          } else {
            setDraggedSkillIndex(index);
          }
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', skill);
        }}
        onDragEnd={(e) => {
          isDraggingRef.current = false;
          clearDragState();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';

          if (isFromCategory && category) {
            if (draggedCategorySkill && draggedCategorySkill.category === category && draggedCategorySkill.index !== index) {
              setDragOverCategorySkill({ category, index });
            } else if (draggedSkill && draggedSkill.source !== category) {
              setDragOverCategorySkill({ category, index });
            }
            setDragOverCategory(category);
          }
        }}
        onDragLeave={() => {
          if (isFromCategory && category) {
            if (dragOverCategorySkill?.category === category && dragOverCategorySkill?.index === index) {
              setDragOverCategorySkill(null);
            }
          }
        }}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (isFromCategory && category) {
            if (draggedSkill) {
              const { skill: skillToMove, source: fromSource } = draggedSkill;
              
              if (fromSource === category) {
                clearDragState();
                return;
              }

              const categorySkillsList = categorySkills[category] || [];
              const newSkills = [...categorySkillsList];
              newSkills.splice(index, 0, skillToMove);

              let sourceSkills: string[] = [];
              if (fromSource !== null) {
                sourceSkills = (categorySkills[fromSource] || []).filter(s => s !== skillToMove);
                setCategorySkills(prev => ({
                  ...prev,
                  [fromSource]: sourceSkills
                }));
              }

              setCategorySkills(prev => ({
                ...prev,
                [category]: newSkills
              }));

              try {
                if (fromSource !== null) {
                  await updateCategorySkills(fromSource, sourceSkills);
                }
                await updateCategorySkills(category, newSkills);
              } catch (err) {
                setCategorySkills(prev => ({
                  ...prev,
                  [fromSource || '']: (prev[fromSource || ''] || []).concat(skillToMove),
                  [category]: categorySkillsList
                }));
              }
            } else if (draggedCategorySkill && dragOverCategorySkill) {
              const { category: srcCat, index: srcIdx } = draggedCategorySkill;

              if (srcCat === category && srcIdx !== dragOverCategorySkill.index) {
                const categorySkillsList = categorySkills[category] || [];
                const newSkills = [...categorySkillsList];
                const [removed] = newSkills.splice(srcIdx, 1);
                newSkills.splice(dragOverCategorySkill.index, 0, removed);
                
                setCategorySkills(prev => ({
                  ...prev,
                  [category]: newSkills
                }));

                try {
                  await updateCategorySkills(category, newSkills);
                } catch (err) {
                  setCategorySkills(prev => ({
                    ...prev,
                    [category]: categorySkillsList
                  }));
                }
              } else if (srcCat !== category) {
                const sourceSkills = categorySkills[srcCat] || [];
                const skillToMove = sourceSkills[srcIdx];
                if (skillToMove) {
                  const categorySkillsList = categorySkills[category] || [];
                  const newSkills = [...categorySkillsList];
                  newSkills.splice(dragOverCategorySkill.index, 0, skillToMove);
                  const updatedSourceSkills = sourceSkills.filter((_, i) => i !== srcIdx);
                  
                  setCategorySkills(prev => ({
                    ...prev,
                    [srcCat]: updatedSourceSkills,
                    [category]: newSkills
                  }));

                  try {
                    await updateCategorySkills(srcCat, updatedSourceSkills);
                    await updateCategorySkills(category, newSkills);
                  } catch (err) {
                    setCategorySkills(prev => ({
                      ...prev,
                      [srcCat]: sourceSkills,
                      [category]: categorySkillsList
                    }));
                  }
                }
              }
            }
          }

          clearDragState();
        }}
      >
        <span className="resume-editor__other-skill-name" draggable="false">{skill}</span>
        <button
          className="resume-editor__other-skill-remove"
          draggable="false"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={async (e) => {
            e.stopPropagation();
            if (isFromCategory && category) {
              const currentSkills = categorySkills[category] || [];
              const updatedSkills = currentSkills.filter((_, i) => i !== index);
              
              setCategorySkills(prev => ({
                ...prev,
                [category]: updatedSkills
              }));

              try {
                await updateCategorySkills(category, updatedSkills);
              } catch (err) {
                setCategorySkills(prev => ({
                  ...prev,
                  [category]: currentSkills
                }));
              }
            }
          }}
        >
          <span className="resume-editor__other-skill-remove-icon" draggable="false">×</span>
        </button>
      </div>
    );
  };

  return (
    <div
      className="resume-editor__section-expanded-body"
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
    >
      <div className="resume-editor__skills-search">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <input
          type="text"
          className={`resume-editor__skills-input ${toast?.type === 'error' ? 'resume-editor__skills-input--error' : ''}`}
          placeholder="Search Specific Skills"
          value={skillSearch}
          onChange={(e) => {
            setSkillSearch(e.target.value);
            if (toast) setToast(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddSkill();
            }
          }}
            style={{ flex: 1 }}
        />
        <button
          className="resume-editor__skills-add-btn"
          onClick={handleAddSkill}
        >
          Add
        </button>
      </div>
      {toast && (
        <div className={`resume-editor__skills-toast resume-editor__skills-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}
        <div className="resume-editor__skills-ai-suggest">
          <span className="resume-editor__skills-ai-text">...or get</span>
          <button 
            className="resume-editor__skills-ai-btn"
            onClick={async () => {
              if (cvUid) {
                try {
                  const entityUid = skillGroupsData.length > 0 
                    ? skillGroupsData[0].uid 
                    : (skillsStructureUid || undefined);
                  
                  const hasSkills = skills.length > 0;
                  
                  const payload: any = {
                    cv_uid: cvUid,
                    entity_type: 'skills_list',
                    improvement_type: hasSkills ? 'current' : 'new',
                  };
                  
                  if (entityUid) {
                    payload.entity_uid = entityUid;
                  }
                  
                  if (hasSkills) {
                    payload.old_value = { value: Array.isArray(skills) ? [...skills] : [skills] };
                  }
                  
                  await api.post(API_ENDPOINTS.aiCv.createTask, payload);
                  if ((window as any).__refreshAITasks) {
                    (window as any).__refreshAITasks();
                  }
                } catch (error: any) {
                  console.error('Failed to create AI CV task for skills list:', error);
                  const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                  showToast(errorMessage, 'error');
                }
              }
            }}
          >
            <Sparkles size={14} className="resume-editor__skills-ai-icon" />
            <span className="resume-editor__skills-ai-label">AI Suggested Skills</span>
          </button>
        </div>
      </div>

      <div className="resume-editor__skills-divider"></div>

      <div className="resume-editor__skills-actions">
        {!isAddingCategory ? (
          <>
            <button
              className="resume-editor__skills-add-category-btn"
              onClick={() => setIsAddingCategory(true)}
            >
              <span className="resume-editor__skills-add-icon">+</span>
              <span className="resume-editor__skills-add-label">Add Category</span>
            </button>
            <button 
              className="resume-editor__ai-suggestions-btn"
              onClick={async () => {
                if (cvUid) {
                  try {
                    const entityUid = skillGroupsData.length > 0 
                      ? skillGroupsData[0].uid 
                      : (skillsStructureUid || undefined);
                    
                    const hasCategories = categories.length > 0 || skillGroupsData.length > 0;
                    
                    const payload: any = {
                      cv_uid: cvUid,
                      entity_type: 'skills_groups',
                      improvement_type: hasCategories ? 'current' : 'new',
                    };

                    if (entityUid) {
                      payload.entity_uid = entityUid;
                    }

                    if (hasCategories && skillGroupsData.length > 0) {
                      payload.old_value = {
                        value: skillGroupsData.map((g) => ({ value: g.skill_group_name })),
                      };
                    }

                    await api.post(API_ENDPOINTS.aiCv.createTask, payload);
                    if ((window as any).__refreshAITasks) {
                      (window as any).__refreshAITasks();
                    }
                  } catch (error: any) {
                    console.error('Failed to create AI CV task for skills groups:', error);
                    const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                    showToast(errorMessage, 'error');
                  }
                }
              }}
            >
              <Sparkles size={14} />
              <span>AI Categorize</span>
            </button>
          </>
        ) : (
          <div className="resume-editor__skills-add-category-wrapper">
            <div className="resume-editor__skills-add-category-form">
              <input
                type="text"
                className="resume-editor__skills-category-input"
                placeholder="Category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCategory();
                  } else if (e.key === 'Escape') {
                    setIsAddingCategory(false);
                    setNewCategoryName('');
                  }
                }}
                autoFocus
              />
              <button
                className="resume-editor__skills-category-save-btn"
                onClick={handleAddCategory}
              >
                Save
              </button>
              <button
                className="resume-editor__skills-category-cancel-btn"
                onClick={() => {
                  setIsAddingCategory(false);
                  setNewCategoryName('');
                  setCategoryMessage(null);
                  setCategoryError(null);
                }}
              >
                Cancel
              </button>
            </div>
            {categoryMessage && (
              <div className={`resume-editor__category-message resume-editor__category-message--${categoryMessage.type}`}>
                {categoryMessage.text}
              </div>
            )}
          </div>
        )}
      </div>

      {categories.map((category) => {
        const categorySkillsList = categorySkills[category] || [];
        const isEditing = editingCategory === category;
        const isDefaultCategory = skillGroupsData.some(
          (group) => group.skill_group_name === category && group.index === 0
        );

        return (
          <div
            key={category}
            className={`resume-editor__skills-category ${dragOverCategory === category ? 'resume-editor__skills-category--drag-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'move';
              if (draggedSkill || draggedCategorySkill) {
                setDragOverCategory(category);
              }
            }}
            onDragLeave={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const { clientX: x, clientY: y } = e;
              if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
                  if (dragOverCategory === category) {
                    setDragOverCategory(null);
                  }
                }
              }
            }}
            onDrop={async (e) => {
              e.preventDefault();
              e.stopPropagation();

              if (draggedSkill) {
                const { skill, source } = draggedSkill;
                
                if (source === category) {
                  clearDragState();
                  return;
                }

                let sourceSkills: string[] = [];
                if (source !== null) {
                  sourceSkills = (categorySkills[source] || []).filter(s => s !== skill);
                  setCategorySkills(prev => ({
                    ...prev,
                    [source]: sourceSkills
                  }));
                }

                const newCategorySkills = [...(categorySkills[category] || []), skill];
                setCategorySkills(prev => ({
                  ...prev,
                  [category]: newCategorySkills
                }));

                try {
                  if (source !== null) {
                    await updateCategorySkills(source, sourceSkills);
                  }
                  await updateCategorySkills(category, newCategorySkills);
                } catch (err) {
                  setCategorySkills(prev => ({
                    ...prev,
                    [source || '']: (prev[source || ''] || []).concat(skill),
                    [category]: (prev[category] || []).filter(s => s !== skill)
                  }));
                }
              } else if (draggedCategorySkill) {
                const { category: srcCat, index: srcIdx } = draggedCategorySkill;
                
                if (srcCat === category) {
                  clearDragState();
                  return;
                }

                const sourceSkills = categorySkills[srcCat] || [];
                const skillToMove = sourceSkills[srcIdx];
                if (skillToMove) {
                  const updatedSourceSkills = sourceSkills.filter((_, i) => i !== srcIdx);
                  const newCategorySkills = [...(categorySkills[category] || []), skillToMove];
                  
                  setCategorySkills(prev => ({
                    ...prev,
                    [srcCat]: updatedSourceSkills,
                    [category]: newCategorySkills
                  }));

                  try {
                    await updateCategorySkills(srcCat, updatedSourceSkills);
                    await updateCategorySkills(category, newCategorySkills);
                  } catch (err) {
                    setCategorySkills(prev => ({
                      ...prev,
                      [srcCat]: sourceSkills,
                      [category]: (prev[category] || []).filter(s => s !== skillToMove)
                    }));
                  }
                }
              }

              clearDragState();
            }}
          >
            <div
              className="resume-editor__skills-category-header"
              ref={(el) => { categoryMenuRefs.current[category] = el; }}
            >
              {isEditing ? (
                <input
                  type="text"
                  className="resume-editor__skills-category-edit-input"
                  value={editingCategoryName}
                  onChange={(e) => setEditingCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRenameCategory(category, editingCategoryName.trim());
                    } else if (e.key === 'Escape') {
                      setEditingCategory(null);
                      setEditingCategoryName('');
                    }
                  }}
                  onBlur={() => {
                    setEditingCategory(null);
                    setEditingCategoryName('');
                  }}
                  autoFocus
                />
              ) : (
                <>
                  <span
                    className="resume-editor__skills-category-title"
                    onClick={() => setOpenCategoryMenu(openCategoryMenu === category ? null : category)}
                  >
                    {category}
                  </span>
                  <span className="resume-editor__skills-category-divider"></span>

                  {openCategoryMenu === category && (
                    <div className="resume-editor__skills-category-menu">
                      <button
                        className="resume-editor__skills-category-menu-item"
                        onClick={() => {
                          setEditingCategory(category);
                          setEditingCategoryName(category);
                          setOpenCategoryMenu(null);
                        }}
                      >
                        Rename
                      </button>
                      {!isDefaultCategory && (
                        <button
                          className="resume-editor__skills-category-menu-item resume-editor__skills-category-menu-item--danger"
                          onClick={() => handleDeleteCategory(category)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div 
              className="resume-editor__skills-category-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (draggedSkill || draggedCategorySkill) {
                  setDragOverCategory(category);
                }
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (draggedSkill) {
                  const { skill, source } = draggedSkill;
                  if (source === category) {
                    clearDragState();
                    return;
                  }

                  let sourceSkills: string[] = [];
                  if (source !== null) {
                    sourceSkills = (categorySkills[source] || []).filter(s => s !== skill);
                    setCategorySkills(prev => ({
                      ...prev,
                      [source]: sourceSkills
                    }));
                  }

                  const newCategorySkills = [...(categorySkills[category] || []), skill];
                  setCategorySkills(prev => ({
                    ...prev,
                    [category]: newCategorySkills
                  }));

                  try {
                    if (source !== null) {
                      await updateCategorySkills(source, sourceSkills);
                    }
                    await updateCategorySkills(category, newCategorySkills);
                  } catch (err) {
                    setCategorySkills(prev => ({
                      ...prev,
                      [source || '']: (prev[source || ''] || []).concat(skill),
                      [category]: (prev[category] || []).filter(s => s !== skill)
                    }));
                  }
                } else if (draggedCategorySkill) {
                  const { category: srcCat, index: srcIdx } = draggedCategorySkill;
                  if (srcCat === category) {
                    clearDragState();
                    return;
                  }

                  const sourceSkills = categorySkills[srcCat] || [];
                  const skillToMove = sourceSkills[srcIdx];
                  if (skillToMove) {
                    const updatedSourceSkills = sourceSkills.filter((_, i) => i !== srcIdx);
                    const newCategorySkills = [...(categorySkills[category] || []), skillToMove];
                    
                    setCategorySkills(prev => ({
                      ...prev,
                      [srcCat]: updatedSourceSkills,
                      [category]: newCategorySkills
                    }));

                    try {
                      await updateCategorySkills(srcCat, updatedSourceSkills);
                      await updateCategorySkills(category, newCategorySkills);
                    } catch (err) {
                      setCategorySkills(prev => ({
                        ...prev,
                        [srcCat]: sourceSkills,
                        [category]: (prev[category] || []).filter(s => s !== skillToMove)
                      }));
                    }
                  }
                }

                clearDragState();
              }}
            >
              {categorySkillsList.length === 0 ? (
                  <div className="resume-editor__skills-category-empty">
                    <span className="resume-editor__skills-category-empty-text">Drag skills here</span>
                  </div>
              ) : (
                <div className="resume-editor__skills-category-list">
                  {categorySkillsList.map((skill, skillIndex) =>
                    renderSkillChip(
                      skill,
                      skillIndex,
                      category,
                      draggedCategorySkill?.category === category && draggedCategorySkill?.index === skillIndex,
                      dragOverCategorySkill?.category === category && dragOverCategorySkill?.index === skillIndex
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
